import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, paymentsAPI } from '../services/api';
import { User, Heart, MessageCircle, Star, Edit2, MapPin, ShoppingBag, Clock, Play, AlertCircle, Camera, Clapperboard } from 'lucide-react';
import ContentCard from '../components/ContentCard';
import api from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [comments, setComments] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showCastingModal, setShowCastingModal] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      setProfile(response.data);
      setEditData({
        username: response.data.username,
        city: response.data.city || '',
        state: response.data.state || ''
      });
      
      // Load user activity
      const commentsRes = await userAPI.getComments(response.data.id);
      setComments(commentsRes.data);
      
      const ratingsRes = await userAPI.getRatings(response.data.id);
      setRatings(ratingsRes.data);

      // Load PPV purchases
      try {
        const purchasesRes = await paymentsAPI.getMyPurchases();
        setPurchases(purchasesRes.data);
      } catch (err) {
        console.log('Could not load purchases');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await userAPI.updateProfile(editData);
      setIsEditing(false);
      loadProfile();
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert('File must be under 500KB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Only JPEG, PNG, and WebP images are allowed');
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/users/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, avatar: res.data.avatar }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Calculate time remaining for countdown
  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return { expired: true, text: 'Expired' };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return { expired: false, text: `${days}d ${hours}h left`, days, hours, minutes };
    } else if (hours > 0) {
      return { expired: false, text: `${hours}h ${minutes}m left`, days, hours, minutes, urgent: hours < 6 };
    } else {
      return { expired: false, text: `${minutes}m left`, days, hours, minutes, urgent: true };
    }
  };

  // Active and expired purchases
  const activePurchases = purchases.filter(p => !p.is_expired);
  const expiredPurchases = purchases.filter(p => p.is_expired);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-electric/30 border-t-electric rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="profile-page">
        <div className="text-center">
          <User className="h-16 w-16 text-foreground/40 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Please Sign In</h2>
          <p className="text-foreground/60 mb-4">You need to be signed in to view your profile.</p>
          <a href="/signin" className="px-6 py-3 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 inline-block">
            Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Profile Header */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-8" style={{ backdropFilter: 'blur(40px) saturate(180%)' }}>
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-6">
              <div className="relative group/avatar">
                <div className="w-24 h-24 rounded-full bg-electric flex items-center justify-center overflow-hidden">
                  {profile.avatar ? (
                    <img src={profile.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-12 w-12 text-background" />
                  )}
                </div>
                <label className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer" data-testid="avatar-upload-btn">
                  {uploadingAvatar ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarUpload} disabled={uploadingAvatar} />
                </label>
              </div>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.username}
                    onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                    className="text-3xl font-bold text-foreground bg-white/10 border border-white/20 rounded-lg px-3 py-2 mb-2"
                    data-testid="edit-username-input"
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-foreground mb-2">{profile.username}</h1>
                )}
                <p className="text-foreground/60 mb-2">{profile.email}</p>
                {(profile.city || profile.state || isEditing) && (
                  <div className="flex items-center space-x-2 text-foreground/70">
                    <MapPin className="h-4 w-4" />
                    {isEditing ? (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          placeholder="City"
                          value={editData.city}
                          onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm"
                          data-testid="edit-city-input"
                        />
                        <input
                          type="text"
                          placeholder="State"
                          value={editData.state}
                          onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm"
                          data-testid="edit-state-input"
                        />
                      </div>
                    ) : (
                      <span>{[profile.city, profile.state].filter(Boolean).join(', ')}</span>
                    )}
                  </div>
                )}
                {!profile.can_comment && (
                  <p className="text-destructive text-sm mt-2">⚠️ Commenting restricted due to community violations</p>
                )}
              </div>
            </div>
            
            <div>
              {isEditing ? (
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90"
                    data-testid="save-profile-btn"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-white/10 text-foreground rounded-lg font-medium hover:bg-white/20"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-foreground rounded-lg font-medium hover:bg-white/20"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="h-4 w-4" />
                    <span>Edit Profile</span>
                  </button>
                  <button
                    onClick={() => setShowCastingModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-electric text-background rounded-lg font-semibold hover:bg-electric/90 transition-all"
                    data-testid="casting-btn"
                  >
                    <Clapperboard className="h-4 w-4" />
                    <span>Casting</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Heart className="h-6 w-6 text-electric mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{profile.stats?.favorites || 0}</p>
              <p className="text-sm text-foreground/60">Favorites</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <MessageCircle className="h-6 w-6 text-electric mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{profile.stats?.comments || 0}</p>
              <p className="text-sm text-foreground/60">Comments</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Star className="h-6 w-6 text-electric mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{profile.stats?.ratings || 0}</p>
              <p className="text-sm text-foreground/60">Ratings</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <ShoppingBag className="h-6 w-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{activePurchases.length}</p>
              <p className="text-sm text-foreground/60">Active PPV</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-white/10 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-electric border-b-2 border-electric'
                : 'text-foreground/60 hover:text-electric'
            }`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('purchases')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap flex items-center space-x-2 ${
              activeTab === 'purchases'
                ? 'text-electric border-b-2 border-electric'
                : 'text-foreground/60 hover:text-electric'
            }`}
            data-testid="tab-purchases"
          >
            <ShoppingBag className="h-4 w-4" />
            <span>Purchases ({purchases.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'comments'
                ? 'text-electric border-b-2 border-electric'
                : 'text-foreground/60 hover:text-electric'
            }`}
            data-testid="tab-comments"
          >
            Comments ({comments.length})
          </button>
          <button
            onClick={() => setActiveTab('ratings')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'ratings'
                ? 'text-electric border-b-2 border-electric'
                : 'text-foreground/60 hover:text-electric'
            }`}
            data-testid="tab-ratings"
          >
            Ratings ({ratings.length})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
              <h3 className="text-xl font-semibold mb-4">Account Information</h3>
              <div className="space-y-2">
                <p><span className="text-foreground/60">Member since:</span> <span className="text-foreground">{new Date(profile.created_at).toLocaleDateString()}</span></p>
                <p><span className="text-foreground/60">Account type:</span> <span className={`${profile.is_premium ? 'text-electric font-medium' : 'text-foreground'}`}>{profile.is_premium ? 'Premium' : 'Free'}</span></p>
                <p><span className="text-foreground/60">Status:</span> <span className={profile.can_comment ? 'text-green-400' : 'text-destructive'}>{profile.can_comment ? 'Active' : 'Restricted'}</span></p>
              </div>
            </div>

            {/* Quick Purchases Preview */}
            {activePurchases.length > 0 && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold flex items-center space-x-2">
                    <ShoppingBag className="h-5 w-5 text-amber-400" />
                    <span>Active PPV Content</span>
                  </h3>
                  <button
                    onClick={() => setActiveTab('purchases')}
                    className="text-sm text-electric hover:underline"
                  >
                    View All
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {activePurchases.slice(0, 2).map((purchase) => {
                    const timeRemaining = getTimeRemaining(purchase.expires_at);
                    return (
                      <div 
                        key={purchase.id} 
                        className="flex items-center space-x-4 p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => navigate(`/content/${purchase.content_id}`)}
                      >
                        <img
                          src={purchase.content?.thumbnail}
                          alt={purchase.content?.title}
                          className="w-20 h-12 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground font-medium truncate">{purchase.content?.title}</p>
                          <div className={`flex items-center space-x-1 text-xs ${timeRemaining.urgent ? 'text-amber-400' : 'text-foreground/60'}`}>
                            <Clock className="h-3 w-3" />
                            <span>{timeRemaining.text}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="space-y-6">
            {/* Active Purchases */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
              <h3 className="text-xl font-semibold mb-4 flex items-center space-x-2">
                <Play className="h-5 w-5 text-green-400" />
                <span>Active Purchases</span>
                <span className="text-sm font-normal text-foreground/60">({activePurchases.length})</span>
              </h3>
              
              {activePurchases.length > 0 ? (
                <div className="grid gap-4">
                  {activePurchases.map((purchase) => {
                    const timeRemaining = getTimeRemaining(purchase.expires_at);
                    const progressPercent = Math.max(0, Math.min(100, 
                      ((7 - (timeRemaining.days || 0)) / 7) * 100
                    ));
                    
                    return (
                      <div 
                        key={purchase.id} 
                        className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/content/${purchase.content_id}`)}
                        data-testid={`purchase-item-${purchase.id}`}
                      >
                        <div className="relative w-32 h-20 flex-shrink-0">
                          <img
                            src={purchase.content?.thumbnail}
                            alt={purchase.content?.title}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                            <Play className="h-8 w-8 text-white" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="text-foreground font-medium mb-1 group-hover:text-electric transition-colors">
                            {purchase.content?.title}
                          </h4>
                          <p className="text-sm text-foreground/60 mb-2">
                            {purchase.content?.content_type?.replace('_', ' ')} • {purchase.content?.year}
                          </p>
                          
                          {/* Countdown Timer */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className={`flex items-center space-x-1 ${timeRemaining.urgent ? 'text-amber-400' : 'text-foreground/60'}`}>
                                <Clock className="h-3 w-3" />
                                <span>{timeRemaining.text}</span>
                              </span>
                              <span className="text-foreground/40">
                                Expires: {new Date(purchase.expires_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full transition-all ${
                                  progressPercent > 80 ? 'bg-amber-400' : 'bg-electric'
                                }`}
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right flex-shrink-0">
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Active
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 bg-white/5 rounded-xl">
                  <ShoppingBag className="h-12 w-12 text-foreground/30 mx-auto mb-3" />
                  <p className="text-foreground/60 mb-2">No active purchases</p>
                  <p className="text-sm text-foreground/40">Browse PPV content to rent your favorites</p>
                </div>
              )}
            </div>

            {/* Expired Purchases */}
            {expiredPurchases.length > 0 && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
                <h3 className="text-xl font-semibold mb-4 flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-foreground/40" />
                  <span className="text-foreground/60">Expired Purchases</span>
                  <span className="text-sm font-normal text-foreground/40">({expiredPurchases.length})</span>
                </h3>
                
                <div className="grid gap-3">
                  {expiredPurchases.map((purchase) => (
                    <div 
                      key={purchase.id} 
                      className="flex items-center space-x-4 p-3 bg-white/5 rounded-xl opacity-60"
                      data-testid={`expired-purchase-${purchase.id}`}
                    >
                      <img
                        src={purchase.content?.thumbnail}
                        alt={purchase.content?.title}
                        className="w-24 h-14 object-cover rounded-lg grayscale"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-foreground/70 font-medium truncate">{purchase.content?.title}</h4>
                        <p className="text-xs text-foreground/40">
                          Expired: {new Date(purchase.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/content/${purchase.content_id}`);
                        }}
                        className="px-3 py-1.5 bg-electric/20 text-electric text-xs rounded-lg hover:bg-electric/30 transition-colors"
                        data-testid={`rent-again-${purchase.id}`}
                      >
                        Rent Again
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
                <p className="text-foreground mb-2">{comment.text}</p>
                <div className="flex items-center space-x-4 text-sm text-foreground/60">
                  <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                  <span>👍 {comment.likes || 0}</span>
                  <span>👎 {comment.dislikes || 0}</span>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <div className="text-center py-12 bg-white/5 rounded-xl">
                <MessageCircle className="h-12 w-12 text-foreground/30 mx-auto mb-3" />
                <p className="text-foreground/60">No comments yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ratings' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {ratings.map((rating) => (
              <ContentCard key={rating.content.id} content={rating.content} />
            ))}
            {ratings.length === 0 && (
              <div className="col-span-full text-center py-12 bg-white/5 rounded-xl">
                <Star className="h-12 w-12 text-foreground/30 mx-auto mb-3" />
                <p className="text-foreground/60">No ratings yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Casting Modal */}
      {showCastingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowCastingModal(false)}>
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="casting-modal">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-electric flex items-center justify-center">
                <Clapperboard className="h-6 w-6 text-background" />
              </div>
              <h2 className="text-xl font-bold text-white">Pulse Media Casting</h2>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              You're about to be redirected to our casting portal where you can apply to be featured in upcoming Pulse Media productions. Submit your application and our team will review it.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://pulsemediacasting.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center space-x-2 py-3 bg-electric text-background rounded-lg font-semibold hover:bg-electric/90 transition-all"
                data-testid="casting-apply-link"
              >
                <span>Apply Now</span>
              </a>
              <button
                onClick={() => setShowCastingModal(false)}
                className="px-6 py-3 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                data-testid="casting-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
